/**
 * Connection Matching Service
 *
 * Matches AI-generated workflow actions with available connections.
 * Used to prompt users to approve/reject using existing connections
 * for newly added actions.
 */

import type {
  ConnectionRequirement,
  ConnectionMatch,
  ConnectionMatchingResult,
} from '@/types/connectionProposal';
import type { ConnectionReferences } from '@/types/connectionReference';
import type { WorkflowDefinition } from '@/types/workflowDefinition';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import {
  getActionHostApiId,
  getActionReferenceName,
  getServiceProviderConnectorId,
  normalizeApiId,
} from '@/utils/connections';

// Debug logging - disable in production
const DEBUG_CONNECTION_MATCHING = false;
const debugLog = (...args: unknown[]) => {
  if (DEBUG_CONNECTION_MATCHING) {
    debugLog('', ...args);
  }
};

// Map of known connector short names to display names
const CONNECTOR_DISPLAY_NAMES: Record<string, string> = {
  office365: 'Office 365',
  azureblob: 'Azure Blob Storage',
  sql: 'SQL Server',
  sharepoint: 'SharePoint',
  outlook: 'Outlook',
  teams: 'Microsoft Teams',
  servicebus: 'Service Bus',
  eventhubs: 'Event Hubs',
  keyvault: 'Azure Key Vault',
  cosmosdb: 'Cosmos DB',
  dynamicscrmonline: 'Dynamics 365',
  salesforce: 'Salesforce',
  twitter: 'Twitter',
  slack: 'Slack',
  gmail: 'Gmail',
  googledrive: 'Google Drive',
  dropbox: 'Dropbox',
  onedrive: 'OneDrive',
  azuread: 'Azure AD',
  http: 'HTTP',
  ftp: 'FTP',
  sftp: 'SFTP',
};

/**
 * Normalize a connector ID to a short name for comparison
 * Handles various formats:
 * - Full path: "/subscriptions/.../managedApis/office365" → "office365"
 * - Service provider: "/serviceProviders/AzureBlob" → "azureblob"
 * - Just the name: "office365" → "office365"
 */
function normalizeConnectorId(connectorId: string): string {
  if (!connectorId) return '';

  // Extract the last segment from paths
  const parts = connectorId.split('/');
  const lastPart = parts[parts.length - 1] || '';

  // Normalize to lowercase and remove special characters
  return lastPart.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Find a connection reference by key (case-insensitive)
 */
function findConnectionReferenceByKey(
  references: ConnectionReferences,
  key: string
): { referenceKey: string; reference: ConnectionReferences[string] } | null {
  if (!key) return null;

  const lowerKey = key.toLowerCase();
  for (const [refKey, ref] of Object.entries(references)) {
    if (refKey.toLowerCase() === lowerKey) {
      return { referenceKey: refKey, reference: ref };
    }
  }
  return null;
}

/**
 * Get display name for a connector
 */
function getConnectorDisplayName(connectorId: string): string {
  const normalized = normalizeConnectorId(connectorId);
  return CONNECTOR_DISPLAY_NAMES[normalized] || formatConnectorName(normalized);
}

/**
 * Format a connector name for display (fallback when not in known list)
 */
function formatConnectorName(name: string): string {
  if (!name) return 'Unknown';

  // Convert camelCase or lowercase to Title Case with spaces
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Recursively collect all operations (actions + triggers) from a workflow
 * definition, including those nested inside Switch cases, ForEach, If/Else, etc.
 */
function collectAllOperations(
  definition: Record<string, unknown> | undefined
): Array<{ name: string; operation: unknown }> {
  if (!definition) return [];
  const results: Array<{ name: string; operation: unknown }> = [];

  // Collect triggers
  const triggers = definition.triggers as Record<string, unknown> | undefined;
  if (triggers) {
    for (const [name, trigger] of Object.entries(triggers)) {
      results.push({ name, operation: trigger });
    }
  }

  // Recursively collect actions (handles Switch cases, ForEach, If/Else nesting)
  function walkActions(actions: Record<string, unknown> | undefined) {
    if (!actions) return;
    for (const [name, action] of Object.entries(actions)) {
      if (!action || typeof action !== 'object') continue;
      const a = action as Record<string, unknown>;
      results.push({ name, operation: a });

      // Switch: cases.{caseName}.actions
      if (a.cases && typeof a.cases === 'object') {
        for (const caseObj of Object.values(a.cases as Record<string, unknown>)) {
          if (caseObj && typeof caseObj === 'object') {
            walkActions((caseObj as Record<string, unknown>).actions as Record<string, unknown> | undefined);
          }
        }
        // Switch default: default.actions
        if (a.default && typeof a.default === 'object') {
          walkActions((a.default as Record<string, unknown>).actions as Record<string, unknown> | undefined);
        }
      }

      // ForEach / Until / If-Else: .actions
      if (a.actions && typeof a.actions === 'object') {
        walkActions(a.actions as Record<string, unknown>);
      }
      // If-Else: .else.actions
      if (a.else && typeof a.else === 'object') {
        walkActions((a.else as Record<string, unknown>).actions as Record<string, unknown> | undefined);
      }
    }
  }

  walkActions(definition.actions as Record<string, unknown> | undefined);
  return results;
}

/**
 * Find compatible connections for a connector ID
 * Matches by normalized connector name (case-insensitive)
 */
export function findCompatibleConnections(
  connectorId: string,
  references: ConnectionReferences
): ConnectionMatch[] {
  const normalizedTarget = normalizeConnectorId(connectorId);
  debugLog(` Finding connections for: ${connectorId} (normalized: ${normalizedTarget})`);

  if (!normalizedTarget) return [];

  const matches: ConnectionMatch[] = [];

  for (const [referenceKey, ref] of Object.entries(references)) {
    const normalizedRef = normalizeConnectorId(ref.api.id);
    debugLog(` Checking reference ${referenceKey}: api.id=${ref.api.id} (normalized: ${normalizedRef})`);

    // Case-insensitive match via normalized IDs
    if (normalizedRef === normalizedTarget) {
      debugLog(` MATCH FOUND: ${referenceKey}`);
      matches.push({
        referenceKey,
        displayName: ref.connectionName || referenceKey,
        connectorId: ref.api.id,
      });
    }
  }

  return matches;
}

/**
 * Find compatible connections for a full API ID (e.g., `/managedApis/sql`).
 * Unlike `findCompatibleConnections`, this preserves the connector type distinction
 * so `/managedApis/sql` won't match `/serviceProviders/sql`.
 *
 * Uses `normalizeApiId` which extracts `managedapis/sql` or `serviceproviders/sql`
 * to ensure type-safe matching.
 */
export function findCompatibleConnectionsByApiId(
  apiId: string,
  references: ConnectionReferences
): ConnectionMatch[] {
  const normalizedTarget = normalizeApiId(apiId);
  debugLog(` Finding connections for apiId: ${apiId} (normalized: ${normalizedTarget})`);

  if (!normalizedTarget) return [];

  const matches: ConnectionMatch[] = [];

  for (const [referenceKey, ref] of Object.entries(references)) {
    const normalizedRef = normalizeApiId(ref.api.id);
    debugLog(` Checking reference ${referenceKey}: api.id=${ref.api.id} (normalized: ${normalizedRef})`);

    // Compare normalized API IDs (preserves type prefix)
    if (normalizedRef !== normalizedTarget) continue;
    // Skip template-apply placeholders. `buildPlaceholderConnectionsConfig`
    // writes shells with `connectionName: "<connector> (needs configuration)"`
    // so the canvas badge can read "incomplete" instead of "not found". They
    // are NOT usable connections — auto-matching a requirement against its
    // own placeholder would surface "(needs configuration)" as the matched
    // display name in the template preview (and let the apply flow believe
    // the requirement is already satisfied).
    if (!isConnectionReferenceComplete(ref)) continue;
    debugLog(` MATCH FOUND: ${referenceKey}`);
    matches.push({
      referenceKey,
      displayName: ref.connectionName || referenceKey,
      connectorId: ref.api.id,
    });
  }

  return matches;
}

/**
 * Analyze a single operation (action or trigger) for connection requirements.
 */
function analyzeOperation(
  operationName: string,
  operation: unknown,
  connectionReferences: ConnectionReferences,
  result: ConnectionMatchingResult
): void {
  const referenceName = getActionReferenceName(operation as { inputs?: unknown });
  if (!referenceName) {
    debugLog(` ${operationName} has no connection reference, skipping`);
    return;
  }

  debugLog(` ${operationName} references connection: ${referenceName}`);

  const existingRefMatch = findConnectionReferenceByKey(connectionReferences, referenceName);
  debugLog(` Reference "${referenceName}" exists in store: ${existingRefMatch ? 'YES' : 'NO'}`);

  let connectorId = getActionHostApiId(operation as { inputs?: unknown }) ?? getServiceProviderConnectorId(operation as { inputs?: unknown });
  if (!connectorId && existingRefMatch) {
    connectorId = existingRefMatch.reference.api.id;
  }
  connectorId = connectorId ?? referenceName;

  const requirement: ConnectionRequirement = {
    actionName: operationName,
    connectorId,
    connectorDisplayName: getConnectorDisplayName(connectorId),
    isNewAction: true,
  };

  const compatibleConnections = findCompatibleConnections(connectorId, connectionReferences);
  debugLog(` Compatible connections for ${connectorId}:`, compatibleConnections);

  if (compatibleConnections.length > 0) {
    let selectedConnection = compatibleConnections[0] || null;
    if (existingRefMatch) {
      const existingMatch = compatibleConnections.find(
        c => c.referenceKey.toLowerCase() === existingRefMatch.referenceKey.toLowerCase()
      );
      if (existingMatch) {
        selectedConnection = existingMatch;
        debugLog(` Pre-selecting existing connection: ${existingRefMatch.referenceKey}`);
      }
    }

    result.needsApproval.push({
      id: crypto.randomUUID(),
      requirement,
      compatibleConnections,
      selectedConnection,
      status: 'pending',
    });
  } else {
    result.noConnectionAvailable.push(requirement);
  }
}

/**
 * Main function: Analyze connection requirements for a proposed workflow.
 * Deterministic — extracts connection references directly from the workflow JSON
 * (host.connection.referenceName or serviceProviderConfiguration.connectionName)
 * rather than inferring from action types.
 */
export function analyzeConnectionRequirements(
  proposedWorkflow: WorkflowDefinition | null,
  baselineWorkflow: WorkflowDefinition | null,
  connectionReferences: ConnectionReferences
): ConnectionMatchingResult {
  const result: ConnectionMatchingResult = {
    needsApproval: [],
    noConnectionAvailable: [],
  };

  if (!proposedWorkflow?.definition) {
    return result;
  }

  // Collect ALL operations (including nested Switch/ForEach/If-Else children + triggers)
  const allProposed = collectAllOperations(proposedWorkflow.definition as unknown as Record<string, unknown>);
  const baselineNames = new Set(
    collectAllOperations(baselineWorkflow?.definition as unknown as Record<string, unknown> | undefined)
      .map(op => op.name)
  );

  // Only analyze new operations (not in baseline)
  const newOperations = allProposed.filter(op => !baselineNames.has(op.name));
  debugLog(' New operations to analyze:', newOperations.map(op => op.name));

  for (const { name, operation } of newOperations) {
    analyzeOperation(name, operation, connectionReferences, result);
  }

  return result;
}

/**
 * Apply approved connection assignments to a workflow.
 * Walks triggers and nested action scopes to find operations by name.
 */
export function applyConnectionAssignments(
  workflow: WorkflowDefinition,
  connectionMatching: ConnectionMatchingResult | undefined
): WorkflowDefinition {
  if (!connectionMatching) {
    return workflow;
  }

  // Deep clone the workflow to avoid mutations
  const updatedWorkflow = JSON.parse(JSON.stringify(workflow)) as WorkflowDefinition;

  for (const proposal of connectionMatching.needsApproval) {
    const name = proposal.requirement.actionName;
    // Look in triggers first, then walk nested actions
    const operation =
      updatedWorkflow.definition?.triggers?.[name] ??
      findOperationInActions(name, updatedWorkflow.definition?.actions as Record<string, unknown> | undefined);
    if (!operation) continue;

    const inputs = (operation as Record<string, unknown>).inputs as Record<string, unknown> | undefined;
    if (!inputs) continue;

    if (proposal.status === 'approved' && proposal.selectedConnection) {
      updateActionConnectionReference(inputs, proposal.selectedConnection.referenceKey);
    } else if (proposal.status === 'rejected') {
      updateActionConnectionReference(inputs, '');
    }
  }

  return updatedWorkflow;
}

/** Walk nested action scopes (Switch/ForEach/If-Else/Until) to find an operation by name. */
function findOperationInActions(name: string, actions: Record<string, unknown> | undefined): unknown | undefined {
  if (!actions) return undefined;
  if (actions[name]) return actions[name];
  for (const action of Object.values(actions)) {
    if (!action || typeof action !== 'object') continue;
    const a = action as Record<string, unknown>;
    const found = findOperationInActions(name, a.actions as Record<string, unknown> | undefined);
    if (found) return found;
    if (a.else && typeof a.else === 'object') {
      const elseFound = findOperationInActions(name, (a.else as Record<string, unknown>).actions as Record<string, unknown> | undefined);
      if (elseFound) return elseFound;
    }
    if (a.cases && typeof a.cases === 'object') {
      for (const c of Object.values(a.cases as Record<string, unknown>)) {
        if (c && typeof c === 'object') {
          const caseFound = findOperationInActions(name, (c as Record<string, unknown>).actions as Record<string, unknown> | undefined);
          if (caseFound) return caseFound;
        }
      }
      if (a.default && typeof a.default === 'object') {
        const defFound = findOperationInActions(name, (a.default as Record<string, unknown>).actions as Record<string, unknown> | undefined);
        if (defFound) return defFound;
      }
    }
  }
  return undefined;
}

/**
 * Update an action's connection reference
 */
function updateActionConnectionReference(
  inputs: Record<string, unknown>,
  referenceKey: string
): void {
  // Handle ApiConnection pattern: inputs.host.connection.referenceName
  if (inputs.host) {
    const host = inputs.host as Record<string, unknown>;
    if (!host.connection) {
      host.connection = {};
    }
    (host.connection as Record<string, unknown>).referenceName = referenceKey;
    return;
  }

  // Handle ServiceProvider pattern: inputs.serviceProviderConfiguration.connectionName
  if (inputs.serviceProviderConfiguration) {
    const spConfig = inputs.serviceProviderConfiguration as Record<string, unknown>;
    spConfig.connectionName = referenceKey;
    return;
  }

  // Handle Agent pattern: inputs.modelConfigurations.{modelN}.referenceName
  // Update model1 first (primary), then fall back to first model with a referenceName
  if (inputs.modelConfigurations) {
    const modelConfigs = inputs.modelConfigurations as Record<string, unknown>;
    const model1 = modelConfigs.model1 as Record<string, unknown> | undefined;
    if (model1) {
      model1.referenceName = referenceKey;
      return;
    }
    for (const value of Object.values(modelConfigs)) {
      if (value && typeof value === 'object') {
        const config = value as Record<string, unknown>;
        if ('referenceName' in config) {
          config.referenceName = referenceKey;
          return;
        }
      }
    }
  }
}

/**
 * Generate the "Required Connections" message for actions without matches
 */
export function generateRequiredConnectionsMessage(
  connectionMatching: ConnectionMatchingResult | undefined
): string | null {
  if (!connectionMatching) return null;

  const unresolved = connectionMatching.noConnectionAvailable;
  if (unresolved.length === 0) return null;

  let message = '📋 **Required Connections**\n';
  message += 'Configure these connections before running the workflow:\n';

  unresolved.forEach((req, index) => {
    message += `${index + 1}. **${req.actionName}** - needs ${req.connectorDisplayName} connection\n`;
  });

  return message;
}
